import { Button, Group, Input, Modal, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { companyApis } from '../../../../../shared/apis/company/apis';
import type { ICompanyRegData, IUpdateCompanyParams } from '../../../../../shared/apis/company/types';
import { queryClient } from '../../../../../shared/queryClient';
import type { AxiosCommonError } from '../../../../../shared/types';
import type { ICompanyData, ICompanyFormValues } from '../../../../../shared/types/company';
import { handleError } from '../../../../ui/err/handleError';
import { showSuccessNotification } from '../../../../ui/notification';
import { queries } from '../../../../../shared/queries';

export const PhoneNumberBox = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 16px;

  > span {
    margin-bottom: 5px;
  }
`;

export const Error = styled.p`
  color: #fa5252;
  font-size: 12px;
  margin-top: 5px;
`;

export const ModalButtonBox = styled.div`
  margin-top: 20px;
  text-align: right;

  button {
    margin-left: 10px;
  }
`;

export interface ICompanyRegModalProps {
  opened: boolean;
  closeModal: () => void;
  isEdit: boolean;
  data?: ICompanyData | null;
}

export default function CompanyRegModal(props: ICompanyRegModalProps) {
  const { opened, closeModal, isEdit, data: editData } = props;
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ICompanyFormValues>();

  // 거래처 수정 시 폼에 데이터 채우기
  useEffect(() => {
    if (isEdit && editData) {
      reset({
        name: editData.name,
        businessNum1: editData.businessNum.split('-')[0],
        businessNum2: editData.businessNum.split('-')[1],
        businessNum3: editData.businessNum.split('-')[2],
        repName: editData.repName,
        memo: editData.memo,
        tel1: editData.tel.split('-')[0],
        tel2: editData.tel.split('-')[1],
        tel3: editData.tel.split('-')[2],
        email: editData.email,
      });
    }
  }, [isEdit]);

  // 모달 닫기 시 폼 초기화
  const close = () => {
    reset({
      name: '',
      businessNum1: '',
      businessNum2: '',
      businessNum3: '',
      repName: '',
      memo: '',
      tel1: '',
      tel2: '',
      tel3: '',
      email: '',
    });
    closeModal();
  };

  // 거래처 등록
  const { mutate: registerCompany } = useMutation(
    ['registerCompany'],
    (data: ICompanyRegData) => companyApis.registerCompany(data),
    {
      onSuccess: () => {
        showSuccessNotification('거래처 등록', '거래처 정보가 성공적으로 저장되었습니다.');
        queryClient.invalidateQueries(['getCompanies']);
        closeModal();
      },
      onError: (error: AxiosError<AxiosCommonError>) => {
        handleError(error);
      },
    },
  );

  // 거래처 수정
  const { mutate: updateCompany } = useMutation(
    ['registerCompany'],
    (data: IUpdateCompanyParams) => companyApis.updateCompany(data),
    {
      onSuccess: () => {
        showSuccessNotification('거래처 등록', '거래처 정보가 성공적으로 저장되었습니다.');
        queryClient.invalidateQueries(['getCompanies']);
        queryClient.invalidateQueries({ queryKey: queries.company.details._def });
        closeModal();
      },
      onError: (error: AxiosError<AxiosCommonError>) => {
        handleError(error);
      },
    },
  );

  // 거래처 목록
  const onSubmitRegistration: SubmitHandler<ICompanyFormValues> = (data) => {
    const { businessNum1, businessNum2, businessNum3, tel1, tel2, tel3, ...rest } = data;
    // 거래처 수정 요청
    if (isEdit) {
      const sendData = {
        email: rest.email,
        name: rest.name,
        repName: rest.repName,
        memo: rest.memo,
        tel: `${tel1}-${tel2}-${tel3}`,
        businessNum: `${businessNum1}-${businessNum2}-${businessNum3}`,
      };
      updateCompany({
        id: editData?.id as number,
        data: sendData,
      });
      return;
    }

    // 거래처 등록 요청
    const newData = {
      email: rest.email,
      name: rest.name,
      businessNum: `${businessNum1}-${businessNum2}-${businessNum3}`,
      repName: rest.repName,
      memo: rest.memo,
      tel: `${tel1}-${tel2}-${tel3}`,
    };
    registerCompany(newData);
  };

  return (
    <Modal title={isEdit ? '거래처 수정' : '거래처 등록'} opened={opened} onClose={close} centered>
      <form onSubmit={handleSubmit(onSubmitRegistration)}>
        <TextInput
          label="거래처명"
          withAsterisk
          placeholder="거래처명을 입력해주세요."
          error={errors?.name?.message}
          data-autofocus
          {...register('name', { required: '거래처명을 입력해주세요.' })}
        />
        <PhoneNumberBox>
          <Input.Wrapper label="사업자 번호" description="사업자 번호를 입력해주세요." withAsterisk>
            <Group>
              <TextInput
                placeholder="000"
                style={{ flex: 1 }}
                maxLength={3}
                error={!!(errors?.businessNum1?.message || errors?.businessNum2?.message || errors?.businessNum3?.message)}
                {...register('businessNum1', {
                  required: '사업자 번호를 입력해주세요.',
                  pattern: { value: /^[0-9]{3}$/, message: '사업자 번호 형식을 확인해주세요.' },
                })}
              />

              <TextInput
                placeholder="00"
                style={{ flex: 1 }}
                maxLength={2}
                error={!!(errors?.businessNum1?.message || errors?.businessNum2?.message || errors?.businessNum3?.message)}
                {...register('businessNum2', {
                  required: '사업자 번호를 입력해주세요.',
                  pattern: { value: /^[0-9]{2}$/, message: '사업자 번호 형식을 확인해주세요.' },
                })}
              />

              <TextInput
                placeholder="00000"
                style={{ flex: 1 }}
                maxLength={5}
                error={!!(errors?.businessNum1?.message || errors?.businessNum2?.message || errors?.businessNum3?.message)}
                {...register('businessNum3', {
                  required: '사업자 번호를 입력해주세요.',
                  pattern: { value: /^[0-9]{5}$/, message: '사업자 번호 형식을 확인해주세요.' },
                })}
              />
            </Group>
          </Input.Wrapper>
        </PhoneNumberBox>
        <Error>{errors?.businessNum1?.message || errors?.businessNum2?.message || errors?.businessNum3?.message}</Error>
        <TextInput
          label="대표자명"
          withAsterisk
          placeholder="대표자명을 입력해주세요."
          mt="md"
          error={errors?.repName?.message}
          {...register('repName', {
            required: '대표자명을 입력해주세요.',
          })}
        />
        <PhoneNumberBox>
          <TextInput
            maxLength={3}
            placeholder="000"
            label="거래처 전화번호"
            withAsterisk
            error={(errors?.tel1?.message || errors?.tel2?.message || errors?.tel3?.message) && true}
            {...register('tel1', {
              required: '전화번호를 입력해주세요',
              pattern: {
                value: /^[0-9]{2,3}$/,
                message: '전화번호 형식을 확인해주세요.',
              },
              minLength: { value: 2, message: '전화번호 형식을 확인해주세요.' },
              maxLength: { value: 3, message: '전화번호 형식을 확인해주세요.' },
            })}
          />
          <span>-</span>
          <TextInput
            maxLength={4}
            placeholder="0000"
            error={(errors?.tel1?.message || errors?.tel2?.message || errors?.tel3?.message) && true}
            {...register('tel2', {
              required: '전화번호를 입력해주세요',
              pattern: {
                value: /^[0-9]{3,4}$/,
                message: '전화번호 형식을 확인해주세요.',
              },
              minLength: { value: 3, message: '전화번호 형식을 확인해주세요.' },
              maxLength: { value: 4, message: '전화번호 형식을 확인해주세요.' },
            })}
          />
          <span>-</span>
          <TextInput
            maxLength={4}
            placeholder="0000"
            error={(errors?.tel1?.message || errors?.tel2?.message || errors?.tel3?.message) && true}
            {...register('tel3', {
              required: '전화번호를 입력해주세요',
              pattern: {
                value: /^[0-9]{4}$/,
                message: '전화번호 형식을 확인해주세요.',
              },
              minLength: { value: 4, message: '전화번호 형식을 확인해주세요.' },
              maxLength: { value: 4, message: '전화번호 형식을 확인해주세요.' },
            })}
          />
        </PhoneNumberBox>
        <Error>{errors?.tel1?.message || errors?.tel2?.message || errors?.tel3?.message}</Error>
        <TextInput
          withAsterisk
          disabled={!!editData?.masterUser}
          label="거래처 이메일"
          placeholder="최고 관리자 최초 생성 시 사용됩니다."
          mt="md"
          error={errors?.email?.message}
          {...register('email', {
            required: '거래처 이메일을 입력해주세요.',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: '이메일 형식을 확인해주세요.',
            },
          })}
        />
        <TextInput label="메모" placeholder="메모를 입력해주세요." mt="md" error={errors?.memo?.message} {...register('memo')} />
        <ModalButtonBox>
          <Button variant="outline" type="button" onClick={close}>
            취소
          </Button>
          <Button type="submit" variant="filled" color="secondary.5">
            저장
          </Button>
        </ModalButtonBox>
      </form>
    </Modal>
  );
}
